/* eslint-disable ember/no-runloop */
import { registerDestructor } from '@ember/destroyable';
import { action, set } from '@ember/object';
import { next } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismModal } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralReportScheduleReportSchedule from 'lumen/components/general/report-schedule/report-schedule';
import GeneralReportScheduleReportsWarningContent from 'lumen/components/general/report-schedule/reports-warning-content';
import getElement from 'lumen/modifiers/get-element';

interface GeneralReportScheduleReportsModalsSignature {
  Args: {
    brands: unknown;
    canCreateNewUser?: boolean;
    canCreateOtherReports?: boolean;
    compareType: unknown;
    compareValue: unknown;
    compsetAvailable?: boolean;
    compsetId: unknown;
    doRefresh: unknown;
    edit: unknown;
    hotel: unknown;
    isShowingScheduleModal?: boolean;
    los: number;
    managementTypes: unknown;
    market: unknown;
    maxPersons: number;
    mealPlan: number;
    membershipType: unknown;
    onCloseScheduleModal: () => void;
    otaId: unknown;
    passedShopconfigs: unknown;
    rateParam: string;
    refreshAfterEdit: unknown;
    reportgroup: unknown;
    roomType: unknown;
    selectedOTA: unknown;
    showMeta?: boolean;
    subtype: unknown;
    type: string;
  };
}

export default class GeneralReportScheduleReportsModals extends Component<GeneralReportScheduleReportsModalsSignature> {
  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked reportsWarningContentElement = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showMeta() {
    return this.args.showMeta ?? true;
  }

  unloadAndRefresh(): void {
    // Unload error (TypeError: Cannot read property 'eachAttribute' of null)
    this.store.unloadAll('reportgroup');
    this.store.unloadAll('reportconfig');

    // https://github.com/emberjs/data/issues/4963
    // schedule('destroy', this, function () {
    //   this.store.unloadAll('reportgroup');
    //   this.store.unloadAll('reportconfig');
    // });

    if (this.args.refreshAfterEdit) {
      // @ts-expect-error: Incorrect type
      this.args.doRefresh();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  saveScheduledReports(errorMessages, warningMessages, successMessages): void {
    this.args.onCloseScheduleModal();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (errorMessages.length || warningMessages.length) {
      set(this, 'reportErrorMessages', errorMessages);
      set(this, 'reportWarningMessages', warningMessages);
      set(this, 'reportSuccessMessages', successMessages);

      next(async () => {
        await this.alerting.showErrorAlert({
          title: this.intl.t('reports.creation_error'),
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          htmlContent: this.reportsWarningContentElement.firstElementChild, // Pass the first child because the reportsWarningContentElement has the 'hidden' attribute.
        });

        set(this, 'reportErrorMessages', null);
        set(this, 'reportWarningMessages', null);
        set(this, 'reportSuccessMessages', null);
        // This function call is duplicated because it can only occur in the current run loop
        // So it can not be taken out of the if/else
        this.unloadAndRefresh();
      });
    } else {
      this.toast.success(this.intl.t('reports.scheduled_reports.success'), {
        showIcon: true,
      });
      this.unloadAndRefresh();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  setReportsWarningContentElement(element): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.reportsWarningContentElement = element;

    registerDestructor(this, () => {
      this.reportsWarningContentElement = null;
    });
  }

  <template>
    {{#if @isShowingScheduleModal}}
      <PrismModal
        @onClose={{@onCloseScheduleModal}}
        @title={{t "reports.schedule_report"}}
      >
        <GeneralReportScheduleReportSchedule
          @brands={{@brands}}
          @canCreateNewUser={{@canCreateNewUser}}
          @canCreateOtherReports={{@canCreateOtherReports}}
          @compareType={{@compareType}}
          @compareValue={{@compareValue}}
          @compsetAvailable={{@compsetAvailable}}
          @compsetId={{@compsetId}}
          @edit={{@edit}}
          @hotelArg={{@hotel}}
          @los={{@los}}
          @managementTypes={{@managementTypes}}
          @marketArg={{@market}}
          @maxPersons={{@maxPersons}}
          @mealPlan={{@mealPlan}}
          @membershipType={{@membershipType}}
          @otaId={{@otaId}}
          @passedShopconfigs={{@passedShopconfigs}}
          @rateParam={{@rateParam}}
          @reportgroup={{@reportgroup}}
          {{! @glint-expect-error: Incorrect type }}
          @roomType={{@roomType}}
          @saveScheduledReports={{this.saveScheduledReports}}
          @selectedOTA={{@selectedOTA}}
          @showMeta={{this.showMeta}}
          @subtype={{@subtype}}
          @type={{@type}}
        />
      </PrismModal>
    {{/if}}
    <div hidden {{getElement this.setReportsWarningContentElement}}>
      <GeneralReportScheduleReportsWarningContent
        {{! @glint-expect-error: Incorrect type }}
        @errorMessages={{this.reportErrorMessages}}
        {{! @glint-expect-error: Incorrect type }}
        @successMessages={{this.reportSuccessMessages}}
        {{! @glint-expect-error: Incorrect type }}
        @warningMessages={{this.reportWarningMessages}}
      />
    </div>
  </template>
}
